import * as THREE from 'three';
import { CAMERA } from '../config/tuning';

const TRAUMA_DECAY = 1.6;
const SHAKE_OFFSET = 0.45;
const SHAKE_ROLL = 0.05;

/**
 * Side-on follow camera: look-ahead grows with speed so the next crest is visible before the
 * player must react; it zooms out in the air and in portrait. Includes trauma-based shake.
 */
export class CameraRig {
  private readonly focus = new THREE.Vector2();
  private lookAhead = 0;
  private zoom = 1;
  private trauma = 0;
  private time = 0;
  reducedMotion = false;

  constructor(private readonly camera: THREE.PerspectiveCamera) {
    camera.fov = CAMERA.fov;
    camera.near = 0.5;
    camera.far = 600;
    camera.updateProjectionMatrix();
  }

  snap(x: number, y: number): void {
    this.focus.set(x, y);
    this.lookAhead = 0;
    this.zoom = 1;
    this.trauma = 0;
    this.apply();
  }

  addTrauma(amount: number): void {
    if (this.reducedMotion) return;
    this.trauma = Math.min(1, this.trauma + amount);
  }

  update(dt: number, x: number, y: number, speed: number, airborne: boolean): void {
    this.time += dt;
    const k = 1 - Math.exp(-CAMERA.followLerp * dt);
    this.focus.x += (x - this.focus.x) * k;
    this.focus.y += (y - this.focus.y) * k;

    const visibleWidth = this.visibleWidth();
    const ahead = Math.max(0, Math.min(1, speed / CAMERA.lookAheadFullSpeed)) * CAMERA.lookAheadMax * visibleWidth;
    this.lookAhead += (ahead - this.lookAhead) * (1 - Math.exp(-2.2 * dt));
    const targetZoom = 1 + (airborne ? CAMERA.zoomOutAir : 0) + Math.min(0.12, Math.max(0, speed - 8) * 0.02);
    this.zoom += (targetZoom - this.zoom) * (1 - Math.exp(-2 * dt));
    this.trauma = Math.max(0, this.trauma - TRAUMA_DECAY * dt);
    this.apply();
  }

  get x(): number {
    return this.camera.position.x;
  }

  get y(): number {
    return this.camera.position.y;
  }

  private distance(): number {
    const portrait = this.camera.aspect < 1;
    return CAMERA.distance * this.zoom * (portrait ? CAMERA.portraitDistanceScale : 1);
  }

  private visibleWidth(): number {
    const height = 2 * this.distance() * Math.tan(THREE.MathUtils.degToRad(this.camera.fov / 2));
    return height * this.camera.aspect;
  }

  private apply(): void {
    const cx = this.focus.x + this.lookAhead;
    const cy = this.focus.y + CAMERA.height;
    this.camera.position.set(cx, cy + 1.2, this.distance());
    this.camera.lookAt(cx, cy, 0);
    if (this.trauma > 0) {
      const s = this.trauma * this.trauma;
      const f = this.time * 31;
      this.camera.position.x += SHAKE_OFFSET * s * noise(f, 1);
      this.camera.position.y += SHAKE_OFFSET * s * noise(f, 2);
      this.camera.rotation.z += SHAKE_ROLL * s * noise(f, 3);
    }
  }
}

function noise(t: number, seed: number): number {
  const x = Math.sin(t * 12.9898 + seed * 78.233) * 43758.5453;
  return (x - Math.floor(x)) * 2 - 1;
}
